import { useEffect, useState, useCallback } from "react";
import { theme, card } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { EvidenceImportPanel } from "../components/EvidenceImportPanel";
import { useCurrentUser } from "../auth/useCurrentUser";
import { safeHref } from "../utils/safeHref";

import { cbSafe } from "../lib/colourBlind";
interface GoogleStatus {
  connected: boolean;
  userEmail: string | null;
  calendarEnabled: boolean;
  driveEnabled: boolean;
  adminEnabled: boolean;
  gmailEnabled: boolean;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  driveFolder: string | null;
  driveFileCount: number;
  adminEventCount: number;
  gmailMessageCount: number;
}

interface GmailMessage {
  id: string;
  messageId: string;
  subject: string;
  fromAddress: string;
  receivedAt: string;
  snippet: string;
  syncedAt: string;
}

interface DriveFile {
  id: string;
  fileId: string;
  name: string;
  mimeType: string;
  webViewLink: string | null;
  modifiedAt: string | null;
  syncedAt: string;
}

interface AdminEvent {
  id: string;
  eventId: string;
  kind: string;
  actorEmail: string | null;
  ipAddress: string | null;
  occurredAt: string;
}

/** A collected-records list (Drive files, Admin Console events): loaded on demand, read-only. */
function CollectedList<T extends { id: string }>({ title, desc, path, field, empty, headers, row }: {
  title: string;
  desc: string;
  path: string;
  field: string;
  empty: string;
  headers: string[];
  row: (item: T) => React.ReactNode[];
}) {
  const [items, setItems] = useState<T[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<Record<string, T[]>>(path);
      setItems(data[field] ?? []);
    } catch (e) { setError(apiErrorMessage(e, `Failed to load ${title.toLowerCase()}.`)); }
    finally { setLoading(false); }
  }

  const cell: React.CSSProperties = { padding: "10px 14px", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
  return (
    <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 16 }}>
      <div style={{ padding: "14px 18px", borderBottom: `1px solid ${theme.color.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>{title}</div>
          <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>{desc}</div>
        </div>
        <button onClick={load} disabled={loading}
          style={{ border: `1px solid ${theme.color.accent}`, background: theme.color.accentBg, borderRadius: 3, padding: "8px 16px", fontSize: 12, fontWeight: 700, color: theme.color.accentText, cursor: "pointer", whiteSpace: "nowrap", opacity: loading ? 0.6 : 1 }}>
          {loading ? "Loading…" : items === null ? "Load" : "Refresh"}
        </button>
      </div>
      {error && <div style={{ padding: "12px 18px", fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
      {items !== null && items.length === 0 && !error && (
        <div style={{ padding: "16px 18px", fontSize: 12, color: theme.color.subtle }}>{empty}</div>
      )}
      {items !== null && items.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--c-line)", background: theme.color.panelAlt }}>
                {headers.map((h) => <th key={h} style={{ padding: "8px 14px", textAlign: "left", fontWeight: 600, color: theme.color.muted, whiteSpace: "nowrap" }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.id} style={{ borderBottom: i < items.length - 1 ? `1px solid ${theme.color.border}` : undefined }}>
                  {row(item).map((c, j) => <td key={j} style={{ ...cell, color: j === 0 ? theme.color.text : theme.color.muted }}>{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Toggle({ enabled, disabled, onToggle, label }: { enabled: boolean; disabled: boolean; onToggle: () => void; label: string }) {
  return (
    <button
      disabled={disabled}
      onClick={onToggle}
      style={{
        flexShrink: 0, width: 44, height: 24, borderRadius: 3, border: "none",
        background: enabled ? theme.color.accent : theme.color.borderStrong,
        cursor: disabled ? "not-allowed" : "pointer",
        position: "relative", transition: "background 0.15s",
        opacity: disabled ? 0.6 : 1,
      }}
      aria-label={`${enabled ? "Disable" : "Enable"} ${label}`}
    >
      <span style={{
        position: "absolute", top: 3, left: enabled ? 23 : 3,
        width: 18, height: 18, borderRadius: 2, background: "#fff",
        transition: "left 0.15s",
      }} />
    </button>
  );
}

export function ConnectGooglePage() {
  const [status, setStatus] = useState<GoogleStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);
  const [driveFolderInput, setDriveFolderInput] = useState("");
  const [savingFolder, setSavingFolder] = useState(false);
  const [banner, setBanner] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [gmailMessages, setGmailMessages] = useState<GmailMessage[]>([]);
  const [loadingGmail, setLoadingGmail] = useState(false);
  const [calSyncing, setCalSyncing] = useState(false);
  const { user } = useCurrentUser();
  // POST /google/calendar-sync is client-only behind integrations:admin.
  const canAdmin = user?.role === "client" && !!user.permissions?.includes("integrations:admin");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "true") {
      setBanner({ kind: "success", text: "Google Workspace connected! Enable the features you want below." });
      window.history.replaceState({}, "", window.location.pathname);
      const t = setTimeout(() => setBanner(null), 6000);
      return () => clearTimeout(t);
    }
    const error = params.get("error");
    if (error === "invalid_state") setBanner({ kind: "error", text: "Connection failed — the link expired. Please try again." });
    else if (error === "auth_failed") setBanner({ kind: "error", text: "Google authorisation failed. Please try again." });
    if (error) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const data = await api.get<GoogleStatus>("/google/status");
      setStatus(data);
      setDriveFolderInput(data.driveFolder ?? "");
    } catch { setBanner({ kind: "error", text: "Failed to load connection status." }); }
    finally { setLoadingStatus(false); }
  }, []);

  const fetchGmailMessages = useCallback(async () => {
    setLoadingGmail(true);
    try {
      const data = await api.get<{ messages: GmailMessage[] }>("/google/gmail/messages");
      setGmailMessages(data.messages);
    } catch { setBanner({ kind: "error", text: "Failed to load Gmail messages." }); }
    finally { setLoadingGmail(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  async function handleConnect() {
    setConnecting(true);
    try {
      const data = await api.get<{ url: string }>("/google/auth-url");
      window.location.href = data.url;
    } catch (err) {
      const msg = (err as { payload?: { message?: string } })?.payload?.message || "Failed to start Google connection. Please try again.";
      setBanner({ kind: "error", text: msg });
      setConnecting(false);
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true);
    try {
      await api.del("/google/disconnect");
      await fetchStatus();
      setBanner({ kind: "success", text: "Google Workspace disconnected." });
    } catch {
      setBanner({ kind: "error", text: "Failed to disconnect. Please try again." });
    } finally { setDisconnecting(false); }
  }

  async function handleToggle(field: string, current: boolean) {
    if (!status) return;
    setToggling(field);
    try {
      await api.patch("/google/settings", { [field]: !current });
      setStatus((s) => s ? { ...s, [field === "calendarEnabled" ? "calendarEnabled" : field === "driveEnabled" ? "driveEnabled" : field === "adminEnabled" ? "adminEnabled" : "gmailEnabled"]: !current } : s);
    } catch {
      setBanner({ kind: "error", text: "Failed to update setting." });
    } finally { setToggling(null); }
  }

  async function handleSaveFolder() {
    setSavingFolder(true);
    try {
      await api.patch("/google/settings", { driveFolder: driveFolderInput.trim() || null });
      setStatus((s) => s ? { ...s, driveFolder: driveFolderInput.trim() || null } : s);
      setBanner({ kind: "success", text: "Drive folder updated." });
    } catch {
      setBanner({ kind: "error", text: "Failed to save folder setting." });
    } finally { setSavingFolder(false); }
  }

  async function handleSync() {
    setSyncing(true);
    try {
      const data = await api.post<{ queued: boolean; syncTypes: string[] }>("/google/sync", {});
      setBanner({ kind: "success", text: `Sync queued for: ${data.syncTypes.join(", ")}. Results will appear shortly.` });
      setTimeout(fetchStatus, 5000);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Sync failed";
      setBanner({ kind: "error", text: msg.includes("no_features_enabled") ? "Enable at least one feature before syncing." : msg });
    } finally { setSyncing(false); }
  }

  async function handleCalendarSync() {
    setCalSyncing(true);
    try {
      const data = await api.post<{ synced: number }>("/google/calendar-sync", {});
      setBanner({ kind: "success", text: `${data.synced === 0 ? "Calendar tasks are already in Google Calendar." : `${data.synced} calendar task${data.synced === 1 ? "" : "s"} added to Google Calendar.`} Register deadlines are being refreshed in the background.` });
    } catch (e) {
      setBanner({ kind: "error", text: apiErrorMessage(e, "Calendar sync failed.") });
    } finally { setCalSyncing(false); }
  }

  const btnPrimary: React.CSSProperties = {
    border: `1px solid ${theme.color.accent}`, background: theme.color.accentBg,
    borderRadius: 3, padding: "8px 16px", fontSize: 12, fontWeight: 700,
    color: theme.color.accentText, cursor: "pointer",
  };

  const FEATURES = [
    {
      key: "calendarEnabled" as const,
      title: "Google Calendar sync",
      desc: "Calendar tasks, audits and every dated obligation from your registers (regulatory submission and incident notification deadlines, data subject request due dates, corrective actions, vulnerability deadlines, supplier contract notice and end dates, certificate and insurance expiries, policy and impact reviews) are added to the connected account's primary Google Calendar, and updated or removed when the record changes.",
      enabled: status?.calendarEnabled ?? false,
    },
    {
      key: "driveEnabled" as const,
      title: "Google Drive evidence",
      desc: "Scans your Drive for compliance-relevant documents (policies, procedures, audit records) and ingests their text as evidence for AI compliance checks.",
      enabled: status?.driveEnabled ?? false,
      badge: status?.driveFileCount ? `${status.driveFileCount} files` : undefined,
    },
    {
      key: "adminEnabled" as const,
      title: "Admin Console audit logs",
      desc: "Collects login events, admin activity, and token usage from Google Workspace Admin Reports. Requires a Google Workspace admin account.",
      enabled: status?.adminEnabled ?? false,
      badge: status?.adminEventCount ? `${status.adminEventCount} events` : undefined,
    },
    {
      key: "gmailEnabled" as const,
      title: "Gmail compliance search",
      desc: "Searches your mailbox for compliance-related emails (incident notifications, audit correspondence, policy reviews) and surfaces them as evidence.",
      enabled: status?.gmailEnabled ?? false,
      badge: status?.gmailMessageCount ? `${status.gmailMessageCount} emails` : undefined,
    },
  ];

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        Connect Google Workspace
      </h1>
      <p style={{ margin: "0 0 24px", fontSize: 13, color: theme.color.subtle }}>
        Connect your Google account to sync schedule events to Google Calendar and collect compliance evidence from Drive, Admin Console, and Gmail.
      </p>

      {banner && (
        <div style={{
          ...card, marginBottom: 20,
          border: `1px solid ${banner.kind === "success" ? theme.color.success : theme.color.danger}`,
          background: banner.kind === "success" ? cbSafe("#16a34a10") : cbSafe("#dc262610"),
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ fontSize: 13, color: banner.kind === "success" ? theme.color.successText : theme.color.dangerText }}>
            {banner.text}
          </span>
          <button onClick={() => setBanner(null)} style={{ background: "none", border: "none", color: theme.color.muted, cursor: "pointer", fontSize: 16 }}>×</button>
        </div>
      )}

      {loadingStatus && <div style={{ ...card, color: theme.color.subtle, fontSize: 13 }}>Loading connection status…</div>}

      {/* ── Connected ── */}
      {!loadingStatus && status?.connected && (
        <>
          <EvidenceImportPanel compact sourceFilter={["google_drive"]} />

          {/* Connection header */}
          <div style={{ ...card, marginBottom: 16, border: `1px solid ${theme.color.success}`, background: cbSafe("#16a34a0a") }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: theme.color.successText, background: cbSafe("#16a34a20"), border: `1px solid ${theme.color.success}`, borderRadius: 3, padding: "3px 10px" }}>
                  Connected
                </span>
                <span style={{ fontSize: 13, color: theme.color.muted }}>{status.userEmail}</span>
                {status.lastSyncAt && (
                  <span style={{ fontSize: 11, color: theme.color.faint }}>
                    Last sync: {new Date(status.lastSyncAt).toLocaleString()}
                  </span>
                )}
                {status.lastSyncError && (
                  <span style={{ fontSize: 11, color: theme.color.dangerText }} title={status.lastSyncError}>
                    Last sync had errors: {status.lastSyncError.length > 120 ? status.lastSyncError.slice(0, 120) + "…" : status.lastSyncError}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  style={{ ...btnPrimary, opacity: syncing ? 0.6 : 1 }}
                >
                  {syncing ? "Syncing…" : "Sync evidence now"}
                </button>
                <button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  style={{ background: "transparent", border: cbSafe("1px solid #7f1d1d"), borderRadius: 3, padding: "7px 14px", fontSize: 12, fontWeight: 600, color: theme.color.dangerText, cursor: disconnecting ? "not-allowed" : "pointer", opacity: disconnecting ? 0.6 : 1 }}
                >
                  {disconnecting ? "Disconnecting…" : "Disconnect"}
                </button>
              </div>
            </div>
          </div>

          {/* Feature toggles */}
          <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ padding: "14px 18px 10px", borderBottom: `1px solid ${theme.color.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Integration features</div>
              <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>
                Re-connect Google if you see permission errors after enabling a new feature.
              </div>
            </div>
            {FEATURES.map((f, i) => (
              <div
                key={f.key}
                style={{
                  display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                  gap: 16, padding: "14px 18px",
                  borderBottom: i < FEATURES.length - 1 ? `1px solid ${theme.color.border}` : undefined,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{f.title}</span>
                    {f.badge && (
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "1px 8px", borderRadius: 3, background: theme.color.accentBg, color: theme.color.accentText }}>
                        {f.badge}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>{f.desc}</div>
                  {f.key === "calendarEnabled" && f.enabled && canAdmin && (
                    <button onClick={handleCalendarSync} disabled={calSyncing}
                      style={{ ...btnPrimary, marginTop: 8, padding: "5px 12px", opacity: calSyncing ? 0.6 : 1 }}>
                      {calSyncing ? "Syncing…" : "Sync now"}
                    </button>
                  )}
                </div>
                <Toggle
                  enabled={f.enabled}
                  disabled={toggling === f.key}
                  onToggle={() => handleToggle(f.key, f.enabled)}
                  label={f.title}
                />
              </div>
            ))}
          </div>

          {/* Drive folder filter */}
          {status.driveEnabled && (
            <div style={{ ...card, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Drive folder filter</div>
              <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 12, lineHeight: 1.6 }}>
                Restrict the Drive scan to a specific folder. Paste the folder ID from the Drive URL (the part after <code>/folders/</code>). Leave blank to scan your entire Drive.
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={driveFolderInput}
                  onChange={(e) => setDriveFolderInput(e.target.value)}
                  placeholder="e.g. 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74"
                  style={{
                    flex: 1, padding: "8px 12px", borderRadius: 3, fontSize: 12,
                    border: `1px solid ${theme.color.border}`, background: theme.color.panel,
                    color: theme.color.text, outline: "none",
                  }}
                />
                <button
                  onClick={handleSaveFolder}
                  disabled={savingFolder || driveFolderInput === (status.driveFolder ?? "")}
                  style={{ ...btnPrimary, opacity: (savingFolder || driveFolderInput === (status.driveFolder ?? "")) ? 0.6 : 1 }}
                >
                  {savingFolder ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          )}

          {status.driveEnabled && (
            <CollectedList<DriveFile>
              title="Google Drive evidence files"
              desc="The most recent 100 compliance-relevant files collected from Drive."
              path="/google/drive/files"
              field="files"
              empty="No Drive files collected yet. Run a sync first."
              headers={["Name", "Type", "Modified", "Collected"]}
              row={(f) => [
                safeHref(f.webViewLink)
                  ? <a href={safeHref(f.webViewLink)} target="_blank" rel="noopener noreferrer" style={{ color: theme.color.accentText }}>{f.name}</a>
                  : f.name,
                f.mimeType.replace(/^application\/vnd\.google-apps\./, "Google ").replace(/^application\//, ""),
                f.modifiedAt ? new Date(f.modifiedAt).toLocaleDateString() : "—",
                new Date(f.syncedAt).toLocaleDateString(),
              ]}
            />
          )}

          {status.adminEnabled && (
            <CollectedList<AdminEvent>
              title="Admin Console events"
              desc="The most recent 200 login, admin and token events from Google Workspace Admin Reports."
              path="/google/admin/events"
              field="events"
              empty="No Admin Console events collected yet. Run a sync first."
              headers={["Event", "Actor", "IP address", "When"]}
              row={(e) => [e.kind, e.actorEmail ?? "—", e.ipAddress ?? "—", new Date(e.occurredAt).toLocaleString()]}
            />
          )}

          {/* Gmail messages panel */}
          {status.gmailEnabled && (
            <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 16 }}>
              <div style={{
                padding: "14px 18px", borderBottom: `1px solid ${theme.color.border}`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Gmail compliance messages</div>
                  <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>
                    Emails matching compliance keywords from the last 90 days. Metadata only — no message bodies are stored.
                  </div>
                </div>
                <button
                  onClick={fetchGmailMessages}
                  disabled={loadingGmail}
                  style={{ ...btnPrimary, opacity: loadingGmail ? 0.6 : 1, whiteSpace: "nowrap" }}
                >
                  {loadingGmail ? "Loading…" : gmailMessages.length === 0 ? "Load messages" : "Refresh"}
                </button>
              </div>
              {gmailMessages.length === 0 && !loadingGmail && (
                <div style={{ padding: "16px 18px", fontSize: 12, color: theme.color.subtle }}>
                  Click "Load messages" to view collected compliance emails. Run a sync first if none appear.
                </div>
              )}
              {gmailMessages.length > 0 && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid var(--c-line)", background: theme.color.panelAlt }}>
                        {["Subject", "From", "Date", "Snippet"].map((h) => (
                          <th key={h} style={{ padding: "8px 14px", textAlign: "left", fontWeight: 600, color: theme.color.muted, whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {gmailMessages.map((m, i) => (
                        <tr key={m.id} style={{ borderBottom: i < gmailMessages.length - 1 ? `1px solid ${theme.color.border}` : undefined }}>
                          <td style={{ padding: "10px 14px", color: theme.color.text, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.subject || "(no subject)"}</td>
                          <td style={{ padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{m.fromAddress}</td>
                          <td style={{ padding: "10px 14px", color: theme.color.faint, whiteSpace: "nowrap" }}>{new Date(m.receivedAt).toLocaleDateString()}</td>
                          <td style={{ padding: "10px 14px", color: theme.color.subtle, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.snippet}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Re-authorise notice */}
          <div style={{ ...card, background: theme.color.panelAlt }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, marginBottom: 4 }}>Need to update permissions?</div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 10, lineHeight: 1.6 }}>
              If you enabled Drive, Admin Console, or Gmail after connecting, Google may need you to re-authorise with the new permissions. Click below to reconnect — your settings are preserved.
            </div>
            <button onClick={handleConnect} disabled={connecting} style={{ ...btnPrimary, opacity: connecting ? 0.6 : 1 }}>
              {connecting ? "Redirecting…" : "Re-authorise Google"}
            </button>
          </div>
        </>
      )}

      {/* ── Disconnected ── */}
      {!loadingStatus && !status?.connected && (
        <div style={card}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14, marginBottom: 20 }}>
            {[
              { title: "Calendar sync", desc: "Calendar tasks, audits and every register deadline are added to your Google Calendar and kept up to date automatically." },
              { title: "Drive evidence", desc: "Compliance documents in Google Drive are ingested as evidence for AI checks." },
              { title: "Admin Console", desc: "Login events and admin activity from Google Workspace become security evidence." },
              { title: "Gmail search", desc: "Compliance-related emails are surfaced as evidence without storing message content." },
            ].map((f) => (
              <div key={f.title} style={{ padding: "14px 16px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panelAlt }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 5 }}>{f.title}</div>
                <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>{f.desc}</div>
              </div>
            ))}
          </div>
          <button
            onClick={handleConnect}
            disabled={connecting}
            style={{ ...btnPrimary, display: "block", width: "100%", padding: "11px 18px", fontSize: 14, opacity: connecting ? 0.7 : 1, marginBottom: 14 }}
          >
            {connecting ? "Redirecting to Google…" : "Connect Google Workspace"}
          </button>
          <p style={{ margin: 0, fontSize: 12, color: theme.color.subtle, lineHeight: 1.7 }}>
            You'll be asked to sign in to your Google account and approve the permissions you need. All features are individually toggleable after connecting. Admin Console features require a Google Workspace admin account.
          </p>
        </div>
      )}
    </div>
  );
}
